import React, { useState } from 'react';
import { 
  PhoneIcon,
  EnvelopeIcon,
  ClockIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState(null);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormStatus('loading');
    
    // Simulate form submission
    setTimeout(() => {
      setFormStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        service: '',
        message: ''
      });
      setTimeout(() => setFormStatus(null), 5000);
    }, 2000);
  };

  const contactMethods = [
    {
      icon: PhoneIcon,
      title: "Phone Support",
      subtitle: "Speak with our experts",
      primary: "+27 74 258 6662",
      secondary: "Call us anytime",
      color: "bg-blue-600",
      available: "24/7 Available"
    },
    {
      icon: ChatBubbleLeftRightIcon,
      title: "WhatsApp",
      subtitle: "Message us directly",
      primary: "+27 62 367 6161",
      secondary: "Quick responses",
      color: "bg-green-600",
      available: "Online now"
    },
    {
      icon: EnvelopeIcon,
      title: "Email Support",
      subtitle: "Get detailed assistance",
      primary: "support@cargohaulco.org",
      secondary: "sales@cargohaul.org",
      color: "bg-orange-500",
      available: "Response within 2 hours"
    },
    {
      icon: ChatBubbleLeftRightIcon,
      title: "Live Chat",
      subtitle: "Instant assistance",
      primary: "Available on website",
      secondary: "Quick responses",
      color: "bg-purple-600",
      available: "Online now"
    },
  ];


  const services = [
    "Express Delivery",
    "International Shipping",
    "Business Solutions",
    "Warehouse Services",
    "Supply Chain Management",
    "Custom Logistics"
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
          {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-blue-700">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80')] bg-cover bg-center mix-blend-overlay opacity-20"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white mb-6">
              Contact Us
            </h1>
          <p className="text-xl sm:text-2xl text-white/90 max-w-4xl mx-auto leading-relaxed">
            Ready to transform your logistics? Let's start the conversation today. 
            Our team is here to help you with all your shipping needs across South Africa.
            </p>
          </div>
      </section>

          {/* Contact Methods */}
      <section className="py-16 bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {contactMethods.map((method, index) => (
              <div 
                key={index} 
                className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-blue-300 hover:shadow-xl transition-all"
              >
                <div className={`w-16 h-16 ${method.color} rounded-xl flex items-center justify-center mb-4`}>
                  <method.icon className="w-8 h-8 text-white" />
                    </div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">{method.title}</h3>
                <p className="text-gray-600 text-sm mb-3">{method.subtitle}</p>
                  <div className="space-y-1 mb-3">
                  <p className="text-gray-900 font-medium">{method.primary}</p>
                  {method.secondary && (
                    <p className="text-gray-600 text-sm">{method.secondary}</p>
                  )}
                  </div>
                <div className="inline-flex items-center gap-2 bg-green-50 rounded-full px-3 py-1">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-xs text-green-700 font-medium">{method.available}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

          {/* Main Content Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white rounded-2xl p-8 border-2 border-gray-200 shadow-lg">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                  <EnvelopeIcon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-black text-gray-900">Send us a Message</h2>
                  <p className="text-gray-600">We'll get back to you within 24 hours</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-gray-50 border-2 border-gray-200 rounded-xl px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-gray-50 border-2 border-gray-200 rounded-xl px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                      placeholder="john@company.com"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full bg-gray-50 border-2 border-gray-200 rounded-xl px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                      placeholder="+27 74 258 6662"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Company</label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="w-full bg-gray-50 border-2 border-gray-200 rounded-xl px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                      placeholder="Your Company"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">Service Interest</label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleInputChange}
                    className="w-full bg-gray-50 border-2 border-gray-200 rounded-xl px-4 py-3 text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                  >
                    <option value="">Select a service</option>
                    {services.map((service, index) => (
                      <option key={index} value={service}>{service}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">Message *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={5}
                    className="w-full bg-gray-50 border-2 border-gray-200 rounded-xl px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all resize-none"
                    placeholder="Tell us about your logistics needs..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={formStatus === 'loading'}
                  className="w-full bg-blue-600 text-white font-bold px-8 py-4 rounded-xl hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
                >
                  {formStatus === 'loading' ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <EnvelopeIcon className="w-5 h-5" />
                    </>
                  )}
                </button>

                {formStatus === 'success' && (
                  <div className="flex items-center gap-2 bg-green-50 border-2 border-green-200 rounded-xl px-4 py-3">
                    <CheckCircleIcon className="w-5 h-5 text-green-600" />
                    <span className="text-green-700 font-medium">Message sent successfully! We'll be in touch soon.</span>
                  </div>
                )}
              </form>
            </div>

            {/* Office Locations */}
            <div className="space-y-6">
              <div className="relative h-[400px] rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1521791136064-7986c2920216?w=800&h=400&fit=crop"
                  alt="Contact Us"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gray-900/60"></div>
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="text-2xl font-black text-white mb-2">Get in Touch</h3>
                  <p className="text-white/90">Our team is ready to help you with all your logistics needs worldwide</p>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

          {/* Emergency Contact */}
      <section className="py-16 bg-orange-50 border-y border-orange-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl p-8 border-2 border-orange-300 shadow-lg">
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center">
                <ExclamationTriangleIcon className="w-6 h-6 text-white" />
              </div>
              <div className="text-center">
                <h3 className="text-2xl font-black text-gray-900">Emergency Support</h3>
                <p className="text-gray-600">24/7 urgent shipment assistance</p>
              </div>
            </div>
            <div className="text-center">
              <p className="text-gray-700 mb-4">For urgent shipment issues or emergency logistics support:</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="tel:+27742586662" className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold px-8 py-4 rounded-xl hover:bg-orange-600 transition-all shadow-lg hover:shadow-xl">
                <PhoneIcon className="w-5 h-5" />
                  Call: +27 74 258 6662
                </a>
                <a href="https://wa.me/27623676161" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-green-500 text-white font-bold px-8 py-4 rounded-xl hover:bg-green-600 transition-all shadow-lg hover:shadow-xl">
                  <ChatBubbleLeftRightIcon className="w-5 h-5" />
                  WhatsApp: +27 62 367 6161
              </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Contact;
